/** Path router for the map vs the Events catalog / one-fire theater. */
import { isDemoPath } from "./demo";

export type AppRoute =
  | { page: "map" }
  | { page: "events"; country: string; month: string; q: string }
  | { page: "theater"; id: string };

const INC_RE = /^inc_[a-z0-9]+$/i;

export function isIncidentId(id: string): boolean {
  return INC_RE.test(id);
}

/** `/events`, `/events/inc_ab12`, or the Europe map. */
export function parseAppPath(pathname: string, search = ""): AppRoute {
  const scoped = isDemoPath(pathname) ? pathname.slice(5) : pathname;
  const path = scoped.replace(/\/+$/, "") || "/";
  if (path === "/events") {
    const q = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    return {
      page: "events",
      country: (q.get("cc") ?? "").trim(),
      month: (q.get("mo") ?? "").trim(),
      q: (q.get("q") ?? "").trim(),
    };
  }
  const m = /^\/events\/(inc_[a-z0-9]+)$/i.exec(path);
  if (m) return { page: "theater", id: m[1]! };
  return { page: "map" };
}

export function eventsCatalogPath(filters?: { country?: string; month?: string; q?: string }): string {
  const q = new URLSearchParams();
  if (filters?.country) q.set("cc", filters.country);
  // `all` is explicit — a missing `mo` means the current month in the UI.
  if (filters?.month === "all") q.set("mo", "all");
  else if (filters?.month) q.set("mo", filters.month);
  if (filters?.q) q.set("q", filters.q);
  const s = q.toString();
  return s ? `/events?${s}` : "/events";
}

export function eventsTheaterPath(id: string): string {
  return `/events/${id}`;
}

/**
 * Old `#e=1` sheet links become `/events` (and `#e=1&id=` becomes the theater).
 * Returns null when the URL is already in the new shape.
 */
export function legacyEventsRedirect(pathname: string, search: string, hash: string): string | null {
  const route = parseAppPath(pathname, search);
  if (route.page !== "map") return null;
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return null;
  const q = new URLSearchParams(raw);
  if (q.get("e") !== "1") return null;
  const id = (q.get("id") ?? "").trim();
  const t = (q.get("t") ?? "").trim();
  const dest = id && isIncidentId(id) ? eventsTheaterPath(id) : "/events";
  return t ? `${dest}#t=${encodeURIComponent(t)}` : dest;
}
